import React, { ReactElement, useRef, useState, useEffect } from "react";
import { connect, FormikContextType } from "formik";
import HypotheekNorm from "../hypotheek-norm/hypotheek-norm";
import { FinancieringOptions } from "../../.generated/forms/formstypes";
import { getVergoedingenOverzichtTextResources } from "../infra/vergoedingen-overzicht-resources";
import {
  LabeledCurrencyInput,
  BerekenCurrencyButton,
  LabeledDateInput,
  LabeledNumberInput,
  LabeledResult,
  bedragFormat,
  optellen,
  WijzigingBevestigenModal
} from "adviesbox-shared";
import { VergoedingenOverzichtState } from "../infra/vergoedingen-overzicht-schema";

const VergoedingenOverzichtCard = ({
  formik
}: {
  formik: FormikContextType<VergoedingenOverzichtState>;
}): ReactElement | null => {
  const [showPopup, setShowPopup] = useState(false);
  const [pendingValue, setPendingValue] = useState<any>(null);
  const prevIngangsdatumRef = useRef(formik.values.vergoedingenOverzicht.ingangsdatum);

  /* istanbul ignore next */
  useEffect(() => {
    const currentValue = formik.values.vergoedingenOverzicht.ingangsdatum;
    if (
      formik.values.heeftNieuweLeningdelen &&
      prevIngangsdatumRef.current !== undefined &&
      String(currentValue) !== String(prevIngangsdatumRef.current)
    ) {
      setPendingValue(currentValue);
      setShowPopup(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.vergoedingenOverzicht.ingangsdatum]);

  /* istanbul ignore next */
  const handlePopupReply = (reply: boolean): void => {
    if (reply === true) {
      prevIngangsdatumRef.current = pendingValue;
    } else {
      formik.setFieldValue("vergoedingenOverzicht.ingangsdatum", prevIngangsdatumRef.current);
    }
    setShowPopup(false);
    setPendingValue(null);
  };

  const {
    advieskostenOvernemen,
    bemiddelingsvergoedingGeldleningOvernemen,
    overigeBemiddelingsvergoedingOvernemen,
    dossiervergoedingOvernemen
  } = formik.values.vergoedingenOverzicht;

  const vergoedingenOverzicht = formik.values.vergoedingenOverzicht;
  const financieringGeselecteerd = formik.values.financiering !== FinancieringOptions.GeenFinanciering;
  const advieskostenPerUur = vergoedingenOverzicht.vergoedingenOverzichtInstellingenTarieven.advieskostenPerUur;
  const advieskostenUurTarief = financieringGeselecteerd
    ? vergoedingenOverzicht.vergoedingenOverzichtInstellingenTarieven.hypotheekAdvieskostenUurTarief
    : vergoedingenOverzicht.vergoedingenOverzichtInstellingenTarieven.advieskostenUurTarief;
  const bemiddelingsvergoedingGeldleningPerUur =
    vergoedingenOverzicht.vergoedingenOverzichtInstellingenTarieven.bemiddelingsvergoedingGeldleningPerUur;
  const bemiddelingsvergoedingGeldleningUurTarief =
    vergoedingenOverzicht.vergoedingenOverzichtInstellingenTarieven.bemiddelingsvergoedingGeldleningUurTarief;
  const overigeBemiddelingsvergoedingPerUur =
    vergoedingenOverzicht.vergoedingenOverzichtInstellingenTarieven.overigeBemiddelingsvergoedingPerUur;
  const overigeBemiddelingsvergoedingUurTarief =
    vergoedingenOverzicht.vergoedingenOverzichtInstellingenTarieven.overigeBemiddelingsvergoedingUurTarief;

  return (
    <>
      <LabeledDateInput
        caption="Verwachte passeerdatum"
        name="vergoedingenOverzicht.ingangsdatum"
        verplicht
        fieldSize={"l"}
        labelColSize={6}
      />

      <HypotheekNorm
        name="vergoedingenOverzicht.hypotheekNormJaarMaand"
        caption="NHG- en hypotheeknorm"
        tooltip={getVergoedingenOverzichtTextResources("TooltipHypotheekNorm")}
      />
      {advieskostenPerUur && (
        <LabeledNumberInput
          caption="(geschatte) uren aan advies"
          name="vergoedingenOverzicht.advieskostenAantalUur"
          appendChildren={<span className="d-flex align-items-center">uur à € {advieskostenUurTarief}</span>}
          fieldSize={"s"}
          labelColSize={6}
        />
      )}

      <LabeledCurrencyInput
        caption="Advieskosten"
        name="vergoedingenOverzicht.advieskostenBedrag"
        readonly={advieskostenOvernemen}
        tooltip={getVergoedingenOverzichtTextResources("TooltipAdvieskosten")}
        labelColSize={6}
        appendChildren={
          <>
            <BerekenCurrencyButton
              name="vergoedingenOverzicht.advieskostenOvernemen"
              berekenen={advieskostenOvernemen}
            />
          </>
        }
      />

      {financieringGeselecteerd && (
        <>
          {bemiddelingsvergoedingGeldleningPerUur && (
            <LabeledNumberInput
              caption="(geschatte) uren bemiddeling geldlening"
              name="vergoedingenOverzicht.bemiddelingsvergoedingGeldleningAantalUur"
              appendChildren={
                <span className="d-flex align-items-center">uur à € {bemiddelingsvergoedingGeldleningUurTarief}</span>
              }
              fieldSize={"s"}
              labelColSize={6}
            />
          )}

          <LabeledCurrencyInput
            caption="Bemiddelingsvergoeding geldlening"
            name="vergoedingenOverzicht.bemiddelingsvergoedingGeldleningBedrag"
            readonly={bemiddelingsvergoedingGeldleningOvernemen}
            labelColSize={6}
            tooltip={getVergoedingenOverzichtTextResources("TooltipBemiddelingsvergoedingGeldlening")}
            appendChildren={
              <>
                <BerekenCurrencyButton
                  name="vergoedingenOverzicht.bemiddelingsvergoedingGeldleningOvernemen"
                  berekenen={bemiddelingsvergoedingGeldleningOvernemen}
                />
              </>
            }
          />
        </>
      )}
      {overigeBemiddelingsvergoedingPerUur && (
        <LabeledNumberInput
          caption="(geschatte) uren overige bemiddeling"
          name="vergoedingenOverzicht.overigeBemiddelingsvergoedingAantalUur"
          labelColSize={6}
          appendChildren={
            <span className="d-flex align-items-center">uur à € {overigeBemiddelingsvergoedingUurTarief}</span>
          }
          fieldSize={"s"}
        />
      )}

      <LabeledCurrencyInput
        caption="Overige bemiddelingsvergoeding"
        name="vergoedingenOverzicht.overigeBemiddelingsvergoedingBedrag"
        labelColSize={6}
        readonly={overigeBemiddelingsvergoedingOvernemen}
        tooltip={getVergoedingenOverzichtTextResources("TooltipOverigeBemiddelingsvergoeding")}
        appendChildren={
          <>
            <BerekenCurrencyButton
              name="vergoedingenOverzicht.overigeBemiddelingsvergoedingOvernemen"
              berekenen={overigeBemiddelingsvergoedingOvernemen}
            />
          </>
        }
      />

      {financieringGeselecteerd && (
        <LabeledCurrencyInput
          caption="Dossiervergoeding geldlening"
          name="vergoedingenOverzicht.dossiervergoedingBedrag"
          readonly={dossiervergoedingOvernemen}
          labelColSize={6}
          tooltip={getVergoedingenOverzichtTextResources("TooltipDossierVergoedingBedrag")}
          appendChildren={
            <>
              <BerekenCurrencyButton
                name="vergoedingenOverzicht.dossiervergoedingOvernemen"
                berekenen={dossiervergoedingOvernemen}
              />
            </>
          }
        />
      )}
      <LabeledResult
        caption="Totaal"
        name="vergoedingenOverzicht.totaal"
        labelColSize={6}
        fontWeight="bold"
        alignRight={true}
        currency={true}
        result={(): string =>
          bedragFormat(
            optellen([
              formik.values.vergoedingenOverzicht.advieskostenBedrag,
              formik.values.vergoedingenOverzicht.bemiddelingsvergoedingGeldleningBedrag,
              formik.values.vergoedingenOverzicht.overigeBemiddelingsvergoedingBedrag,
              formik.values.vergoedingenOverzicht.dossiervergoedingBedrag
            ]),
            0,
            0,
            false
          )
        }
      />

      <WijzigingBevestigenModal
        show={showPopup}
        modalTitle="Wijziging verwachte passeerdatum"
        message="Door deze wijziging is een herberekening van de hypotheek in het voorstel nodig."
        handleConfirm={/* istanbul ignore next */ () => handlePopupReply(true)}
        handleDeny={/* istanbul ignore next */ () => handlePopupReply(false)}
      />
    </>
  );
};

export default connect<{}, VergoedingenOverzichtState>(VergoedingenOverzichtCard);
